import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import CollectionList from './CollectionList';
import DocumentTable from './DocumentTable';
import DocumentEditor from './DocumentEditor';
import useDatabase from './hooks/useDatabase';
import CollapsibleSidebar from '../../../common/CollapsibleSidebar';
import { Loader2, Database } from 'lucide-react';
import { useReadOnlyMode } from '../../../../hooks/useReadOnlyMode';

const DatabaseViewer = forwardRef(({ workspaceId }, ref) => {
  const { isReadOnly } = useReadOnlyMode();
  const {
    collections,
    documents,
    currentCollection,
    loading,
    error,
    fetchCollections,
    fetchDocuments,
    createDocument,
    updateDocument,
    deleteDocument,
    setCurrentCollection,
    setError
  } = useDatabase(workspaceId);

  const [selectedCollection, setSelectedCollection] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingDocument, setDeletingDocument] = useState(null);

  useEffect(() => {
    fetchCollections();
  }, [workspaceId]);

  // Expose refresh method to parent
  useImperativeHandle(ref, () => ({
    refresh: () => {
      // Refresh collections
      fetchCollections();
      // If a collection is selected, refresh its documents
      if (selectedCollection) {
        fetchDocuments(selectedCollection.name);
      }
    }
  }), [selectedCollection, fetchCollections, fetchDocuments]);

  const handleCollectionSelect = (collection) => {
    setSelectedCollection(collection);
    setCurrentCollection(collection.name);
    fetchDocuments(collection.name);
  };

  const handlePageChange = (page) => {
    if (selectedCollection) {
      fetchDocuments(selectedCollection.name, { page });
    }
  };

  const handleSearch = (searchQuery) => {
    if (selectedCollection) {
      // If search query is empty, fetch without filter
      if (!searchQuery.trim()) {
        fetchDocuments(selectedCollection.name);
      } else {
        // Get keys from first document
        const documentKeys = Object.keys(documents?.documents?.[0] || {});
        
        // Only create filter if we have fields to search
        if (documentKeys.length > 0) {
          const filter = {
            $or: documentKeys.map(field => ({
              [field]: { $regex: searchQuery, $options: 'i' }
            }))
          };
          fetchDocuments(selectedCollection.name, { filter });
        } else {
          // Handle empty collection case
          console.warn('Cannot search: No documents available to determine searchable fields');
          // Show user-friendly error message
          setError('Search is not available for empty collections');
          // Clear the search after a few seconds
          setTimeout(() => setError(null), 3000);
        }
      }
    }
  };

  const handleCreateDocument = () => {
    if (isReadOnly) return;
    setEditingDocument(null);
    setShowEditor(true);
  };

  const handleEditDocument = (document) => {
    if (isReadOnly) return;
    setEditingDocument(document);
    setShowEditor(true);
  };

  const handleSaveDocument = async (documentData) => {
    if (isReadOnly) return;
    
    try {
      if (editingDocument) {
        // Update existing document
        await updateDocument(
          selectedCollection.name, 
          editingDocument._id.$oid, 
          documentData
        );
      } else {
        // Create new document
        await createDocument(selectedCollection.name, documentData);
        // Refresh collections to update document count after creation
        fetchCollections();
      }
      setShowEditor(false);
      setEditingDocument(null);
    } catch (error) {
      console.error('Error saving document:', error);
      alert('Failed to save document: ' + error.message);
    }
  };

  const handleDeleteDocument = (document) => {
    if (isReadOnly) return;
    setDeletingDocument(document);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (isReadOnly) return;
    if (!deletingDocument) return;
    
    try {
      await deleteDocument(selectedCollection.name, deletingDocument._id.$oid);
      setShowDeleteConfirm(false);
      setDeletingDocument(null);
      // Refresh collections to update document count
      fetchCollections();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document: ' + error.message);
    }
  };

  if (loading.collections && !collections.length) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-neutral-900">
        <div className="flex flex-col items-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <p className="mt-2 text-ui-text-secondary">Loading database...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-neutral-900">
        <div className="text-center p-4">
          <p className="text-red-600 dark:text-red-400 font-medium">Database Error</p>
          <p className="text-ui-text-secondary mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-gray-50 dark:bg-neutral-900">
      {/* Left Sidebar - Collections */}
      <CollapsibleSidebar
        width={256}
        className="bg-white dark:bg-neutral-800 border-r border-neutral-200 dark:border-neutral-700"
      >
        <CollectionList
          collections={collections}
          selectedCollection={selectedCollection}
          onSelectCollection={handleCollectionSelect}
          loading={loading.collections}
        />
      </CollapsibleSidebar>

      {/* Main Content - Documents */}
      <div className="flex-1 min-w-0 overflow-hidden">
        {selectedCollection ? (
          <DocumentTable
            collection={selectedCollection}
            documents={documents}
            loading={loading.documents}
            onRefresh={() => fetchDocuments(selectedCollection.name)}
            onPageChange={handlePageChange}
            onCreateDocument={handleCreateDocument}
            onEditDocument={handleEditDocument}
            onDeleteDocument={handleDeleteDocument}
            onSearch={handleSearch}
            isReadOnly={isReadOnly}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-ui-text-secondary px-4">
            <Database size={48} className="mb-4 text-neutral-400" />
            <p className="text-center text-sm sm:text-base">Select a collection to view documents</p>
          </div>
        )}
      </div>
      
      {/* Document Editor Modal */}
      <DocumentEditor
        isOpen={showEditor}
        onClose={() => {
          setShowEditor(false);
          setEditingDocument(null);
        }}
        document={editingDocument}
        onSave={handleSaveDocument}
        title={editingDocument ? 'Edit Document' : 'Create Document'}
        isReadOnly={isReadOnly}
      />
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl p-6 max-w-md">
            <h3 className="text-lg font-medium text-ui-text-primary mb-4">
              Delete Document
            </h3>
            <p className="text-ui-text-secondary mb-6">
              Are you sure you want to delete this document? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingDocument(null);
                }}
                className="px-4 py-2 text-sm text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm bg-red-600 text-white hover:opacity-90 rounded-md transition-all duration-200"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default DatabaseViewer;